import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FormikTextarea } from '../../../capacity/containers/IMODescription/IMODescriptionDrawer';
import Drawer from '../../../common/components/Drawer/Drawer';
import { useSnackbar } from 'notistack';
import { Field, Form, Formik } from 'formik';
import FormikInputField from '../../../common/components/FormikInputField/FormikInputField';
import {
  McButton,
  McInputDate,
  McSegmentedControl,
  McSegmentedControlItem,
  McSwitch,
  McTooltip,
} from '@maersk-global/mds-react-wrapper';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import FormattedMessage from '../../../common/FormattedMessage';
import { holidaysService } from '../../../common/service/holidays/holidays-service';
import { Holiday } from '../../../common/service/holidays/types';
import TimeGroup, { TimeSlot } from '../time-group';

type HolidaysDrawerProps = {
  open: boolean;
  onSuccess?: Function;
  onClose: () => void;
  selectedTerminal: string;
  defaultValues?: Holiday;
  action: 'add' | 'edit' | 'add-next';
};

const schema = z.object({
  date: z.string(),
  name: z.string(),
  repeat: z.string(),
  gate: z.string(),
  vesselOps: z.string(),
  remarks: z.string().optional(),
});

const HolidaysDrawer: FunctionComponent<HolidaysDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  selectedTerminal,
  defaultValues,
  action,
}) => {
  const [gateTimeSlots, setGateTimesSlots] = useState<TimeSlot[]>([]);
  const [vesselOpsTimeSlots, setVesselOpsTimesSlots] = useState<TimeSlot[]>([]);
  const [gateSlotsError, setGateSlotsError] = useState(false);
  const [vesselOpsSlotsError, setVesselOpsSlotsError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const service = useMemo(() => holidaysService(), []);

  useEffect(() => {
    setGateTimesSlots(defaultValues?.gateTimeSlots || []);
    setVesselOpsTimesSlots(defaultValues?.vesselOpsTimeSlots || []);
  }, [defaultValues]);

  const getFormInitialValues = () => {
    return action === 'add' || action === 'add-next'
      ? {
          terminalCode: selectedTerminal,
          date: '',
          name: '',
          repeat: 'SAME_DATE',
          gate: 'NO',
          vesselOps: 'NO',
          remarks: '',
          gateTimeSlots: [],
          vesselOpsTimeSlots: [],
        }
      : (defaultValues as Holiday);
  };

  const onSubmit = async (values, { setSubmitting }) => {
    if (gateSlotsError || vesselOpsSlotsError) return;
    setSubmitting(true);
    const shouldAppendGateTimeSlots = gateTimeSlots.length > 0;
    const shouldAppendVesselOpsTimeSlots = vesselOpsTimeSlots.length > 0;
    const dto = {
      ...values,
      ...(shouldAppendGateTimeSlots ? { gateTimeSlots: gateTimeSlots } : { gateTimeSlots: [] }),
      ...(shouldAppendVesselOpsTimeSlots ? { vesselOpsTimeSlots: vesselOpsTimeSlots } : { vesselOpsTimeSlots: [] }),
    };
    try {
      if (action === 'edit') {
        await service.updateHoliday(dto);
        enqueueSnackbar('Holiday updated successfully', { variant: 'success' });
      } else {
        await service.createHoliday(dto);
        enqueueSnackbar('Holiday added successfully', { variant: 'success' });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={action === 'add' ? 'Add new holiday' : 'Edit holiday'}
      open={open}
      setOpen={onClose}
      onRequestClose={onClose}
      noFooter
      wider
    >
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form className="HolidaysDrawer__form">
            <div className="HolidaysDrawer__content">
              <div style={{ width: 180 }}>
                <McInputDate
                  invalid={Boolean(errors.date) || (defaultValues?.date === null && values.date === null)}
                  errormessage={
                    (errors.date as string) ||
                    (defaultValues?.date === null && values.date === null
                      ? `This holiday requires a new date for ${new Date().getFullYear() + 1}`
                      : '')
                  }
                  min={
                    action === 'add'
                      ? `${new Date().getFullYear()}-01-01`
                      : action === 'add-next'
                        ? `${new Date().getFullYear() + 1}-01-01`
                        : `${new Date(values.date || defaultValues?.tempDate || '').getFullYear()}-01-01`
                  }
                  max={
                    action === 'add'
                      ? `${new Date().getFullYear()}-12-31`
                      : action === 'add-next'
                        ? `${new Date().getFullYear() + 1}-12-31`
                        : `${new Date(values.date || defaultValues?.tempDate || '').getFullYear()}-12-31`
                  }
                  label="Date"
                  value={values.date || defaultValues?.tempDate || ''}
                  // @ts-ignore
                  input={(e) => setFieldValue('date', e.target.value)}
                  disabled={action === 'edit' && !defaultValues?.isPrimary && !defaultValues?.tempDate}
                />
              </div>
              <br />
              <Field
                label="Name"
                name="name"
                disabled={action === 'edit' && !defaultValues?.isPrimary}
                component={FormikInputField}
                errormessage={errors.name}
                invalid={Boolean(errors.name)}
              />
              {action === 'edit' && !defaultValues?.isPrimary ? null : (
                <>
                  <br />

                  <div style={{ marginBottom: 8 }}>Repeat every year?</div>
                  <McSegmentedControl>
                    <McTooltip width={400}>
                      <McSegmentedControlItem
                        slot="trigger"
                        label="Same date"
                        value="SAME_DATE"
                        selected={values.repeat === 'SAME_DATE'}
                        onClick={() => setFieldValue('repeat', 'SAME_DATE')}
                      />
                      <span>
                        Use this option to input holidays that repeat every year on the same date. If exceptionally
                        holiday is moved to another date, still select 'Same Date' and modify the entry for next year.
                      </span>
                    </McTooltip>
                    <McTooltip width={400}>
                      <McSegmentedControlItem
                        slot="trigger"
                        label="Different date"
                        value="DIFFERENT_DATE"
                        selected={values.repeat === 'DIFFERENT_DATE'}
                        onClick={() => setFieldValue('repeat', 'DIFFERENT_DATE')}
                      />
                      <span>
                        Use this option to input holidays that are not repeated the same date every year. If
                        exceptionally the holiday date is changed in current year only (but not in the future), select
                        'Same date' and modify the entry for next year.
                      </span>
                    </McTooltip>
                    <McTooltip width={400}>
                      <McSegmentedControlItem
                        slot="trigger"
                        label="No repeat"
                        value="NO_REPEAT"
                        selected={values.repeat === 'NO_REPEAT'}
                        onClick={() => setFieldValue('repeat', 'NO_REPEAT')}
                      />
                      <span>
                        Use this options to input holidays that are one off or they repeat only every few years.
                      </span>
                    </McTooltip>
                  </McSegmentedControl>
                </>
              )}
              <br />
              <br />

              <div className="HolidaysDrawer__periods">
                <McSwitch
                  disabled={!values.date}
                  name="gate"
                  style={{ display: 'inline-flex', marginRight: 32 }}
                  fit="small"
                  label="Gate affected"
                  checked={values.gate === 'YES' || values.gate === 'PARTIAL'}
                  change={() =>
                    setFieldValue('gate', values.gate === 'YES' || values.gate === 'PARTIAL' ? 'NO' : 'YES')
                  }
                />
                <McSegmentedControl id="gate" fit="small">
                  <McSegmentedControlItem
                    disabled={values.gate === 'NO'}
                    selected={values.gate === 'YES'}
                    onClick={() => {
                      setFieldValue('gate', 'YES');
                      setGateTimesSlots([]);
                      setGateSlotsError(false);
                    }}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">All day</span>
                  </McSegmentedControlItem>
                  <McSegmentedControlItem
                    disabled={values.gate === 'NO'}
                    selected={values.gate === 'PARTIAL'}
                    onClick={() => {
                      setFieldValue('gate', 'PARTIAL');
                      setGateSlotsError(false);
                    }}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">Selected period</span>
                  </McSegmentedControlItem>
                </McSegmentedControl>

                {(values.gate === 'YES' || values.gate === 'PARTIAL') && (
                  <TimeGroup
                    isAllDay={values.gate === 'YES'}
                    slots={values.gateTimeSlots || []}
                    setSlots={setGateTimesSlots}
                    setError={setGateSlotsError}
                    date={values.date}
                  />
                )}
              </div>

              <div className="HolidaysDrawer__periods">
                <McSwitch
                  disabled={!values.date}
                  name="vesselOps"
                  style={{ display: 'inline-flex', marginRight: 32 }}
                  fit="small"
                  label="Vessel Ops affected"
                  checked={values.vesselOps === 'YES' || values.vesselOps === 'PARTIAL'}
                  change={() =>
                    setFieldValue(
                      'vesselOps',
                      values.vesselOps === 'YES' || values.vesselOps === 'PARTIAL' ? 'NO' : 'YES',
                    )
                  }
                />
                <McSegmentedControl fit="small">
                  <McSegmentedControlItem
                    disabled={values.vesselOps === 'NO'}
                    selected={values.vesselOps === 'YES'}
                    onClick={() => {
                      setFieldValue('vesselOps', 'YES');
                      setVesselOpsSlotsError(false);
                    }}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">All day</span>
                  </McSegmentedControlItem>
                  <McSegmentedControlItem
                    disabled={values.vesselOps === 'NO'}
                    selected={values.vesselOps === 'PARTIAL'}
                    onClick={() => setFieldValue('vesselOps', 'PARTIAL')}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">Selected period</span>
                  </McSegmentedControlItem>
                </McSegmentedControl>
                {(values.vesselOps === 'YES' || values.vesselOps === 'PARTIAL') && (
                  <TimeGroup
                    isAllDay={values.vesselOps === 'YES'}
                    slots={values.vesselOpsTimeSlots || []}
                    setSlots={setVesselOpsTimesSlots}
                    setError={setVesselOpsSlotsError}
                    date={values.date}
                  />
                )}
              </div>
              <br />
              <Field label="Remarks" type="text" name="remarks" rows={2} maxlength={100} component={FormikTextarea} />
            </div>
            <div className="HolidaysDrawer__buttons">
              <McButton type="button" appearance="neutral" click={onClose}>
                <FormattedMessage id="cancel" />
              </McButton>
              <McButton disabled={isSubmitting} type="submit" appearance="primary">
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </McButton>
            </div>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default HolidaysDrawer;
